<template>
    <div>
        <app-layout>
            <template v-slot:header>
                <Header :title="$t('coop_event_applications')"
                        :isColumns="true"
                        @filter-div-status="datatable.filterStatus = $event">
                    <template v-slot:columns>
                        <div class="mb-1" v-for="(column, key) in datatable.columns" :key="key">
                            <b-form-checkbox
                                :id="'checkbox-' + key"
                                :name="'checkbox-' + key"
                                :value="false"
                                :unchecked-value="true"
                                v-model="column.hidden"
                                v-if="column.field !== 'buttons'"
                            >
                                {{ column.label }}
                            </b-form-checkbox>
                        </div>
                    </template>
                </Header>
            </template>
            <template v-slot:header-mobile>
                <HeaderMobile :title="$t('coop_event_applications')"
                              :isColumns="true"
                              @filter-div-status="datatable.filterStatus = $event" />
            </template>

            <b-button variant="outline-secondary" class="mb-3" @click="$router.push('/coop/admin/event')">
                <i class="ri-arrow-left-line"></i> {{ $t('back') }}
            </b-button>

            <datatable-filter v-show="datatable.filterStatus"
                              @filter="filter"
                              @filterClear="filterClear"
                              :export-excel="true"
                              @exportExcel="$refs.datatable.excel()">
                <b-row>
                    <b-col sm="6" md="4">
                        <b-form-group :label="$t('faculty')">
                            <faculty-selectbox v-model="datatable.queryParams.filter.faculty_code" />
                        </b-form-group>
                    </b-col>
                    <b-col sm="6" md="4">
                        <b-form-group :label="$t('department')">
                            <department-selectbox :faculty_code="datatable.queryParams.filter.faculty_code"
                                                  v-model="datatable.queryParams.filter.department_code" />
                        </b-form-group>
                    </b-col>
                    <b-col sm="6" md="4">
                        <b-form-group :label="$t('class')">
                            <classes-selectbox v-model="datatable.queryParams.filter.class" />
                        </b-form-group>
                    </b-col>
                    <b-col sm="6" md="4">
                        <b-form-group :label="$t('academic_year')">
                            <academic-years-selectbox v-model="datatable.queryParams.filter.academic_year" />
                        </b-form-group>
                    </b-col>
                    <b-col sm="6" md="4">
                        <b-form-group :label="$t('semester')">
                            <semesters-selectbox v-model="datatable.queryParams.filter.semester" />
                        </b-form-group>
                    </b-col>
                    <b-col sm="6" md="4">
                        <b-form-group :label="$t('status')">
                            <b-form-select v-model="datatable.queryParams.filter.status" :options="statusFilterOptions" />
                        </b-form-group>
                    </b-col>
                    <b-col sm="6" md="4">
                        <b-form-group :label="$t('student_number')">
                            <b-form-input v-model="datatable.queryParams.filter.student_number" />
                        </b-form-group>
                    </b-col>
                    <b-col sm="6" md="4">
                        <b-form-group :label="$t('coop_company')">
                            <b-form-select v-model="datatable.queryParams.filter.company_id" :options="companyOptions" />
                        </b-form-group>
                    </b-col>
                </b-row>
            </datatable-filter>

            <datatable ref="datatable"
                       :isLoading.sync="datatable.isLoading"
                       :columns="datatable.columns"
                       :rows="datatable.rows"
                       :total="datatable.total"
                       :queryParams="datatable.queryParams"
                       :lineNumbers="false"
                       :select-options="true"
                       @on-page-change="onPageChange"
                       @on-sort-change="onSortChange"
                       @on-per-page-change="onPerPageChange"
                       @on-selected-rows-change="onSelectedRowsChange"
                       v-show="datatable.showTable"
            />

            <div class="mt-3" v-if="datatable.showTable">
                <b-button variant="primary" @click="approveAll">
                    {{ $t('coop_event_approve_all') }}
                </b-button>
            </div>

            <CommonModal ref="statusModal" size="md" :onHideOnlyX="true">
                <template v-slot:CommonModalTitle>
                    {{ $t('coop_update_status') }}
                </template>
                <template v-slot:CommonModalContent>
                    <b-form-group :label="$t('status')">
                        <b-form-select v-model="selectedStatus" :options="statusOptions" />
                    </b-form-group>
                    <div class="d-flex justify-content-center mt-2">
                        <b-button variant="primary" @click="saveStatus">{{ $t('save') }}</b-button>
                    </div>
                </template>
            </CommonModal>
        </app-layout>
    </div>
</template>
<script>
    //Template
    import AppLayout from '@/layouts/AppLayout';
    import Header from '@/layouts/AppLayout/Header';
    import HeaderMobile from '@/layouts/AppLayout/HeaderMobile';

    //Component
    import DatatableFilter from '@/components/datatable/DatatableFilter';
    import Datatable from '@/components/datatable/Datatable';
    import CommonModal from '@/components/elements/CommonModal';
    import FacultySelectbox from '@/components/interactive-fields/FacultySelectbox.vue';
    import DepartmentSelectbox from '@/components/interactive-fields/DepartmentSelectbox';
    import ClassesSelectbox from '@/components/interactive-fields/ClassesSelectbox';
    import AcademicYearsSelectbox from '@/components/interactive-fields/AcademicYearsSelectbox';
    import SemestersSelectbox from '@/components/interactive-fields/SemestersSelectbox';

    //Service
    import CoopEventApplicationService from '@/services/CoopEventApplicationService';
    import CoopCompanyService from '@/services/CoopCompanyService';

    //Other
    import qs from 'qs';

    export default {
        components: {
            AppLayout,
            Header,
            HeaderMobile,
            DatatableFilter,
            Datatable,
            CommonModal,
            FacultySelectbox,
            DepartmentSelectbox,
            ClassesSelectbox,
            AcademicYearsSelectbox,
            SemestersSelectbox
        },
        metaInfo() {
            return {
                title: this.$t('coop_event_applications')
            };
        },
        data() {
            return {
                selectedApplication: null,
                selectedStatus: null,
                selectedRows: [],
                companyOptions: [],
                statusOptions: [
                    {value: 'approved', text: this.$t('approved')},
                    {value: 'rejected', text: this.$t('rejected')},
                    {value: 'pending', text: this.$t('pending')}
                ],
                statusFilterOptions: [
                    {value: null, text: ''},
                    {value: 'approved', text: this.$t('approved')},
                    {value: 'rejected', text: this.$t('rejected')},
                    {value: 'pending', text: this.$t('pending')}
                ],
                datatable: {
                    isLoading: false,
                    columns: [
                        {
                            label: '',
                            field: 'buttons',
                            html: true,
                            hidden: false,
                            tdClass: 'p-0 text-center w-40 align-middle',
                            buttons: [
                                {
                                    text: this.$t('coop_update_status'),
                                    class: 'ri-refresh-line',
                                    callback: (row) => {
                                        this.showStatusModal(row);
                                    }
                                },
                                {
                                    text: this.$t('coop_event_delete_application'),
                                    class: 'ri-delete-bin-2-line',
                                    callback: (row) => {
                                        this.deleteApplication(row);
                                    }
                                },
                                {
                                    text: this.$t('view'),
                                    class: 'ri-eye-line',
                                    callback: (row) => {
                                        this.$router.push('/coop/student/postings/' + row.event_id);
                                    }
                                }
                            ]
                        },
                        {
                            label: this.toUpperCase('student_number'),
                            field: 'student_number',
                            hidden: false
                        },
                        {
                            label: this.toUpperCase('coop_company'),
                            field: 'company_name',
                            hidden: false
                        },
                        {
                            label: this.toUpperCase('name'),
                            field: 'name',
                            hidden: false
                        },
                        {
                            label: this.toUpperCase('surname'),
                            field: 'surname',
                            hidden: false
                        },
                        {
                            label: this.toUpperCase('department'),
                            field: 'department_name',
                            hidden: false
                        },
                        {
                            label: this.toUpperCase('participation'),
                            field: 'participation',
                            hidden: false
                        },
                        {
                            label: this.toUpperCase('coop_event_name'),
                            field: 'event_name',
                            hidden: false
                        },
                        {
                            label: this.toUpperCase('status'),
                            field: 'status',
                            formatFn: (status) => {
                                const map = {
                                    approved: this.$t('approved'),
                                    rejected: this.$t('rejected'),
                                    pending: this.$t('pending')
                                };
                                return map[status] || status;
                            },
                            hidden: false
                        }
                    ],
                    rows: [],
                    total: 0,
                    filterStatus: true,
                    showTable: false,
                    queryParams: {
                        filter: {},
                        sort: 'id',
                        page: 1,
                        limit: 20
                    }
                }
            };
        },
        created() {
            this.getRows();
            CoopCompanyService.getAll({params: {filter: {}, page: 1, limit: 1000}}).then((response) => {
                this.companyOptions = [{value: null, text: ''}].concat(
                    response.data.data.map((c) => ({value: c.id, text: c.name}))
                );
            });
        },
        methods: {
            filter() {
                this.datatable.queryParams.page = 1;
                this.getRows();
            },
            filterClear() {
                this.datatable.queryParams.filter = {};
                this.getRows();
            },
            onPageChange(params) {
                this.datatable.queryParams.page = params.currentPage;
                this.getRows();
            },
            onPerPageChange(params) {
                this.datatable.queryParams.limit = params.currentPerPage;
                this.datatable.queryParams.page = 1;
                this.getRows();
            },
            onSortChange(params) {
                const sortType = params[0].type == 'desc' ? '-' : '';
                this.datatable.queryParams.sort = sortType + params[0].field;
                this.getRows();
            },
            onSelectedRowsChange(params) {
                this.selectedRows = params.selectedRows;
            },
            getRows() {
                this.datatable.showTable = true;
                this.datatable.isLoading = true;

                const config = {
                    params: {...this.datatable.queryParams},
                    paramsSerializer: (params) => qs.stringify(params, {encode: false})
                };

                return CoopEventApplicationService.getAll(this.$route.params.id, config)
                    .then((response) => {
                        this.datatable.rows = response.data.data;
                        this.datatable.total = response.data.pagination.total;
                    })
                    .finally(() => {
                        this.datatable.isLoading = false;
                    });
            },
            showStatusModal(row) {
                this.selectedApplication = row;
                this.selectedStatus = row.status;
                this.$refs.statusModal.$refs.commonModal.show();
            },
            saveStatus() {
                CoopEventApplicationService.updateStatus(this.selectedApplication.id, this.selectedStatus).then(() => {
                    this.$toast.success(this.$t('coop_status_updated_success'));
                    this.$refs.statusModal.$refs.commonModal.hide();
                    this.getRows();
                });
            },
            deleteApplication(row) {
                this.$swal.fire({
                    text: this.$t('are_you_sure_to_delete'),
                    showCancelButton: true,
                    confirmButtonText: this.$t('yes'),
                    cancelButtonText: this.$t('no')
                }).then((result) => {
                    if (result.isConfirmed) {
                        CoopEventApplicationService.remove(row.id).then(() => {
                            this.$toast.success(this.$t('coop_event_application_deleted_success'));
                            this.getRows();
                        });
                    }
                });
            },
            approveAll() {
                this.$swal.fire({
                    text: this.$t('coop_event_approve_all_confirm'),
                    showCancelButton: true,
                    confirmButtonText: this.$t('yes'),
                    cancelButtonText: this.$t('no')
                }).then((result) => {
                    if (result.isConfirmed) {
                        CoopEventApplicationService.approveAll(this.$route.params.id).then(() => {
                            this.$toast.success(this.$t('coop_event_approve_all_success'));
                            this.getRows();
                        });
                    }
                });
            }
        }
    };
</script>
